const liveViewEventsUrl = () => `${window.postbuildEnv.DEFAULT_URL}/api/v1/live_view_events`;

const HEARTBEAT_INTERVAL_MS = 60000;
const RETRY_DELAY_MS = 2000;

interface LiveViewEvent {
  event_type: 'started' | 'prompt_completed' | 'heartbeat' | 'stopped',
  activity_session_uid: string,
  prompt_id?: string,
  prompt_index?: number,
  optimal?: boolean,
  total_prompts?: number|null,
  time_spent_ms?: number,
  question_time_spent_ms?: number|null,
  stage?: LiveViewStage,
  idle?: boolean,
  unsent_prompt_ids?: string[],
}

export type LiveViewStage = 'directions' | 'onboarding' | 'reading' | 'proofreading' | 'prompts';

interface ReportedPrompt {
  id: string,
  optimal: boolean,
  index?: number,
  sent: boolean,
}

export interface LiveViewSession {
  // Report a prompt the student has finished
  reportPrompt: (promptId: string, optimal: boolean, index?: number) => void,
  hasReported: (promptId: string) => boolean,
  // Call when the student may have moved to another stage or gone idle (or
  // come back); sends the change straight away rather than with the next
  // heartbeat, so the teacher's Time Spent stops and starts with the tool's
  reportState: () => void,
  stop: () => void,
}

export interface LiveViewSessionOptions {
  totalPrompts: number|null,
  // Each tool already tracks idle-excluded active time (timeTracking, saved
  // on the active session); every event carries its current sum so Time Spent
  // in the live view matches the tool's own calculation.
  getTimeSpentMs?: () => number,
  // Where the student is for tools whose progress is not a prompt count
  getStage?: () => LiveViewStage|null,
  // Whether the tool has stopped counting time for inactivity
  getIdle?: () => boolean,
  // The tool's active time on the question the student is on, or null when
  // they are not on a question; drives May Need Help: Long Time on Question
  getQuestionTimeSpentMs?: () => number|null,
}

const enabled = (): boolean => {
  try {
    return !!window.postbuildEnv.LIVE_VIEW_ENABLED;
  } catch (e) {
    return false;
  }
};

const sumTimeTracking = (timeTracking?: { [key: string]: number }|null): number => (
  Object.values(timeTracking || {}).reduce((sum, ms) => sum + (Number(ms) || 0), 0)
);

// Fire-and-forget: live view reporting must never affect the student's session.
// A failed request is retried once; the server dedupes and orders prompts.
const postLiveViewEvent = (event: LiveViewEvent, onResponse?: (body: any) => void, retriesLeft = 1): void => {
  try {
    if (!enabled()) { return; }

    const retry = () => {
      if (retriesLeft > 0) { window.setTimeout(() => postLiveViewEvent(event, onResponse, retriesLeft - 1), RETRY_DELAY_MS); }
    };
    fetch(liveViewEventsUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(event),
    }).then((response) => {
      if (!response.ok) { retry(); return; }
      if (onResponse && response.status !== 204) { response.json().then(onResponse).catch(() => {}); }
    }).catch(retry);
  } catch (e) {
    // ignore
  }
};

// Owns one student session's reporting: the started event, a heartbeat while
// the tab is visible, a stopped beacon on the way out, and a full resync
// (started + every prompt reported so far) when the server has lost the row
// or the page comes back from the back/forward cache.
const startLiveViewSession = (activitySessionUid: string, { totalPrompts, getTimeSpentMs, getStage, getIdle, getQuestionTimeSpentMs }: LiveViewSessionOptions): LiveViewSession => {
  const reported: ReportedPrompt[] = [];
  const currentStage = () => (getStage ? getStage() : null);
  // Reporting must never break the activity, whatever state the tool is in
  const currentIdle = () => {
    try {
      return getIdle ? !!getIdle() : false;
    } catch (e) {
      return false;
    }
  };
  const currentQuestionTimeMs = () => {
    try {
      const ms = getQuestionTimeSpentMs();
      return typeof ms === 'number' ? Math.round(ms) : null;
    } catch (e) {
      return null;
    }
  };
  let lastSentStage: LiveViewStage|null = null;
  let lastSentIdle = false;
  const timeSpent = () => ({
    ...(getTimeSpentMs ? { time_spent_ms: Math.round(getTimeSpentMs()) } : {}),
    ...(getQuestionTimeSpentMs ? { question_time_spent_ms: currentQuestionTimeMs() } : {}),
    ...(currentStage() ? { stage: currentStage() } : {}),
    idle: currentIdle(),
  });
  const post = (event: Omit<LiveViewEvent, 'activity_session_uid'>, onResponse?: (body: any) => void) => {
    lastSentStage = currentStage();
    lastSentIdle = currentIdle();
    postLiveViewEvent({ activity_session_uid: activitySessionUid, ...timeSpent(), ...event, }, onResponse);
  };

  // Prompt-level progress is only sent while a teacher is watching the
  // classroom (the server says so on started and heartbeat responses);
  // presence is always sent so the online count works for every teacher
  let watching = false;

  const sendPrompt = (prompt: ReportedPrompt) => {
    prompt.sent = true;
    post({ event_type: 'prompt_completed', prompt_id: prompt.id, optimal: prompt.optimal, prompt_index: prompt.index, });
  };

  // The server lost the row (or the page came back from the cache): start it
  // again with every prompt so far; it ignores prompts it already has
  const resendAll = (onResponse?: (body: any) => void) => {
    post({ event_type: 'started', total_prompts: totalPrompts, }, onResponse);
    reported.forEach(sendPrompt);
  };

  // Lists held-back answers so the report can wait for them once a teacher watches
  const heartbeat = () => post({ event_type: 'heartbeat', unsent_prompt_ids: reported.filter(prompt => !prompt.sent).map(prompt => prompt.id), }, handleResponse);

  const handleResponse = (body) => {
    if (!body) { return; }

    watching = !!body.watching;
    if (body.resync) {
      resendAll();
      return;
    }
    // Prompts finished while nobody was watching go out once someone is
    if (watching) { reported.filter(prompt => !prompt.sent).forEach(sendPrompt); }
  };


  post({ event_type: 'started', total_prompts: totalPrompts, }, handleResponse);

  const intervalId = window.setInterval(() => {
    // A hidden tab is not a student at work; let presence lapse
    if (document.hidden) { return; }
    heartbeat();
  }, HEARTBEAT_INTERVAL_MS);

  // Save and Exit is a plain link and students also just close the tab, so
  // tell the server on the way out; sendBeacon survives the page unloading
  const reportStopped = () => {
    try {
      if (!enabled()) { return; }
      const body = new Blob([JSON.stringify({ event_type: 'stopped', activity_session_uid: activitySessionUid, ...timeSpent(), })], { type: 'application/json' });
      navigator.sendBeacon(liveViewEventsUrl(), body);
    } catch (e) {
      // ignore
    }
  };
  // pagehide also fires on back/forward navigation; if the page is restored
  // from the cache the student is back at work and must be put back online
  const reportRestored = (event: PageTransitionEvent) => { if (event.persisted) { resendAll(handleResponse); } };
  window.addEventListener('pagehide', reportStopped);
  window.addEventListener('pageshow', reportRestored);

  return {
    reportPrompt: (promptId, optimal, index) => {
      if (reported.some(prompt => prompt.id === promptId)) { return; }
      const prompt = { id: promptId, optimal, index, sent: false, };
      reported.push(prompt);
      if (watching) { sendPrompt(prompt); }
    },
    hasReported: (promptId) => reported.some(prompt => prompt.id === promptId),
    reportState: () => {
      if (currentStage() === lastSentStage && currentIdle() === lastSentIdle) { return; }
      heartbeat();
    },
    stop: () => {
      window.clearInterval(intervalId);
      window.removeEventListener('pagehide', reportStopped);
      window.removeEventListener('pageshow', reportRestored);
    },
  };
};

export {
  postLiveViewEvent,
  sumTimeTracking,
  startLiveViewSession,
};
